import { Dispatch, UnknownAction } from '@reduxjs/toolkit';
import { NavigateFunction } from 'react-router-dom';
import { APP_URLS } from '../../consts';
import { Game, GameType } from './games.model';
import { resolveGameDetailsBasePath } from '../../utils/games/gameDetailsBasePath';

/* eslint-disable @typescript-eslint/ban-ts-comment */
const requestFullscreenAttribute = [
  'requestFullscreen',
  'mozRequestFullScreen',
  'webkitRequestFullscreen',
  'msRequestFullscreen',
  // @ts-ignore
].find((fnName) => typeof document.body?.[fnName] === 'function');

const closeFullscreenAttribute = [
  'exitFullscreen',
  'mozCancelFullScreen',
  'webkitExitFullscreen',
  'msExitFullscreen',
  // @ts-ignore
].find((fnName) => typeof document[fnName] === 'function');

export const requestFullscreen = (el: HTMLElement): Promise<void> => {
  if (!requestFullscreenAttribute) {
    return Promise.reject(new Error('Request fullscreen not supported for device'));
  }

  try {
    // @ts-ignore
    const promise = el[requestFullscreenAttribute]();

    if (promise) {
      return promise.catch((err: { name: string; message: string }) => {
        console.log(`An error occurred while trying to switch into fullscreen mode: ${err.message} (${err.name})`);
      });
    }

    return Promise.resolve();
  } catch (e) {
    return Promise.reject(e);
  }
};

export type GameUrlOptions = {
  basePath?: string;
  gameType?: GameType;
};

const normalizeBasePath = (basePath: string): string => {
  return basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
};

export const getGameUrl = (gameUrl: string, playType?: 'demo' | 'real', options?: GameUrlOptions): string => {
  let defaultBasePath = '/game-details';

  // Use appropriate base path for bingo games
  if (options?.gameType === GameType.Bingo) {
    defaultBasePath = '/bingo-details';
  }

  const basePath = normalizeBasePath(options?.basePath ?? defaultBasePath);
  const baseUrl = `${basePath}/${gameUrl}`;

  if (playType === 'real') {
    return `${baseUrl}/real`;
  }

  if (playType === 'demo') {
    return `${baseUrl}#/demo`;
  }

  return baseUrl;
};

export const getMiniGamePlayUrl = (game: Game): string => {
  const { basePath } = resolveGameDetailsBasePath(game.brand, game.gameType);
  const playPath = getGameUrl(game.friendlyUrlName, 'real', { basePath, gameType: game.gameType });

  return `${window.location.origin}${playPath}${playPath.includes('?') ? '&' : '?'}minigame=true`;
};

export const closeFullscreen = (): void => {
  // @ts-ignore
  if (closeFullscreenAttribute) document[closeFullscreenAttribute]();
};

export const isDocumentFullscreen = (): boolean =>
  ['fullscreenElement', 'mozFullScreenElement', 'webkitFullscreenElement', 'msFullscreenElement'].some(
    // @ts-ignore
    (att) => !!document[att]
  );

export const hasInPathName = (path: string): boolean => {
  return window.location.pathname.includes(path);
};

/**
 * Handle carousel navigation click as a separate route to navigate and expose selected category
 *
 * @param staticData
 * @param setters
 * @param hooks
 */
export const toggleCategoryNavigation = async (
  {
    gridMode,
    title,
    category,
    categorySlug,
  }: { gridMode: boolean; title?: string; category?: string; categorySlug?: string },
  {
    setGridMode,
    setSelectedCategory,
  }: { setGridMode: (arg: boolean) => void; setSelectedCategory: (arg: string) => UnknownAction },
  { dispatch, navigate }: { dispatch: Dispatch; navigate: NavigateFunction }
): Promise<void> => {
  if (gridMode) {
    await navigate(`${APP_URLS.gameLobby.main}/${category}`);
    dispatch(setSelectedCategory(''));
  } else {
    await navigate(`${APP_URLS.gameLobby.main}/${category}/${categorySlug}`);
    dispatch(setSelectedCategory(title || ''));
  }

  setGridMode(!gridMode);
};
